// Pure item-backed mesh presentation resolver.
//
// Supersedes demos/xz3d/item-3d-asset-presentation.js, which targeted the retired
// item_3d_assets table and carried two real bugs this file fixes:
//
//   1. It read pictures.item -- the TEMPLATE bucket -- so INSTANCE art
//      (inventory_presentation_picture / encounter_creature_picture) was invisible to it. A GM who
//      uploaded custom art for one token would silently get the generic template picture.
//   2. It returned ONE winner (ranked[0]). Multi-part items cannot work that way:
//      bracer_left.glb + bracer_right.glb are ONE item that must render BOTH.
//
// Selection consumes only the compiled full-item shape. It never fetches or embeds GLB
// bytes and never recurses through a mesh as though it were another item.

const STATE_FALLBACK = 'default';
const PROFILE_FALLBACK = 'any';
const TOKEN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const PART = /^[a-z0-9][a-z0-9_-]{0,31}$/;
const SHA256 = /^[a-f0-9]{64}$/;
const ROLES = new Set(['world', 'inventory', 'equipped', 'projectile', 'effect']);
const PROFILES = new Set([
  'any', 'masculine_standard', 'masculine_strong', 'feminine_standard', 'feminine_strong',
]);
// Storage tiers, best first. 'automatic' is a REQUEST, never a stored value.
const TIERS = ['high', 'medium', 'low'];
const ALLOWED_ORIGINS = ['https://cdn.fatetide.com/'];
const DAMAGE_STATE_DEFAULT = 'default';
const DAMAGE_STATE_DAMAGED = 'damaged';
const DAMAGE_STATE_BROKEN = 'broken';
const DAMAGED_VITALITY_RATIO = 0.5;

const SPACE_CODE = 0x20;
const DEL_CODE = 0x7f;

function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function token(value, fallback) {
  return typeof value === 'string' && TOKEN.test(value) ? value : fallback;
}

function isActorRow(itemArr) {
  const inventoryId = itemArr?.inventory?.inventory_id ?? itemArr?.inventory_id;
  return isRecord(itemArr?.encounter_creatures)
    && (inventoryId === undefined || inventoryId === null || inventoryId === '');
}

/** A FULL definition from the page's root catalogs (the caches + the creator island), never a tree walk. */
function catalogDefinition(templateId) {
  const ft = globalThis.FateTide;
  const roots = [];
  for (const name of ['casterCache', 'objectCache', 'encounterCache']) {
    const cache = ft?.[name];
    if (cache && typeof cache.values === 'function') for (const root of cache.values()) roots.push(root);
  }
  if (isRecord(globalThis.ftCreatorCharacter)) roots.push(globalThis.ftCreatorCharacter);
  // Catalogs first, across EVERY root (the order `_ftInventoryTemplateDefJS` used) …
  for (const root of roots) {
    const cat = isRecord(root?.catalog) ? root.catalog : null;
    const def = cat?.[templateId] ?? cat?.[templateId.toUpperCase()] ?? cat?.[templateId.toLowerCase()];
    if (isRecord(def) && isRecord(def.item_layer_types)) return def;
  }
  // … then a fetched definition that is its own root (the finder's root-direct match), never a runtime instance.
  const wanted = templateId.toUpperCase();
  for (const root of roots) {
    const rootId = Array.isArray(root?.items?.item_id) ? root.items.item_id[0] : root?.items?.item_id;
    if (isRecord(root) && isRecord(root.item_layer_types) && !isRuntimeInstance(root)
      && String(rootId ?? '').toUpperCase() === wanted) return root;
  }
  return null;
}

/** Twin of object-card/find-cached-item.ts isRuntimeItemInstance: inventory rows, actor rows, PCs and statuses. */
function isRuntimeInstance(node) {
  if (node?.inventory?.inventory_id !== undefined || node?.inventory_id !== undefined) return true;
  if (node?.encounter_creatures?.encounter_creature_id !== undefined || node?.encounter_creature_id !== undefined) return true;
  if (isRecord(node?.player_characters)) return true;
  return node?.status_id !== undefined || node?.status_i !== undefined;
}

function meshTemplateDefinition(itemArr) {
  // v38 inventory template bridge: repeated assets['3d'] rows live once in the host .catalog.
  // Prefer a riding catalog (inv_p / explicitly attached root); then use the established global
  // catalog resolver. A missing/legacy catalog simply preserves the old empty result.
  let templateId = itemArr?.items?.item_id
    ?? itemArr?.inventory?.inventory_item_id
    ?? itemArr?.inventory_item_id;
  if (Array.isArray(templateId)) templateId = templateId[0];
  if (typeof templateId !== 'string' || templateId === '') return null;
  const catalog = isRecord(itemArr?.catalog) ? itemArr.catalog : null;
  let def = catalog?.[templateId]
    ?? catalog?.[templateId.toUpperCase()]
    ?? catalog?.[templateId.toLowerCase()];
  // An ACTOR row (encounter creature / PC, not an inventory instance) names its creature ITEM, whose definition lives
  // in a root catalog (the def store since Round 8). Answer from the catalogs only: when none holds it in full (a
  // private creature item), the finder walk can only miss — and the routing signature asked on every encounter delta,
  // one full walk per move per such actor (PRES lane census 2026-09-27).
  if (!isRecord(def) && isActorRow(itemArr)) return catalogDefinition(templateId);
  if (!isRecord(def) && typeof globalThis._ftInventoryTemplateDefJS === 'function') {
    def = globalThis._ftInventoryTemplateDefJS(itemArr);
  }
  if (!isRecord(def) && typeof globalThis.ftFindFullNestedItem === 'function') {
    def = globalThis.ftFindFullNestedItem(templateId);
  }
  return isRecord(def) ? def : null;
}

function meshesOf(itemArr) {
  let transform = itemArr?.inventory?.transform_3d;
  if (typeof transform === 'string') {
    try { transform = JSON.parse(transform); } catch { transform = null; }
  }
  if (isRecord(transform) && transform.schema === 'ft.inventory-transform-3d.v1'
    && transform.renderMode === 'standee') return [];
  const bucket = isRecord(itemArr?.assets) ? itemArr.assets : null;
  if (Array.isArray(bucket?.['3d'])) return bucket['3d'];
  const def = meshTemplateDefinition(itemArr);
  const defBucket = isRecord(def?.assets) ? def.assets : null;
  return Array.isArray(defBucket?.['3d']) ? defBucket['3d'] : [];
}

function scalarNumber(value) {
  const raw = Array.isArray(value) ? value[0] : value;
  if (raw === null || raw === undefined || raw === '') return null;
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
}

/** Five ordered looks: empty, then four equal positive-capacity bands. */
function meshFullnessLevel(value) {
  const n = scalarNumber(value);
  if (n === null || n < 0) return null;
  return n === 0 ? 0 : Math.min(4, 1 + Math.floor(n * 4));
}

function presentationCapacity(entity) {
  // Instance bucket wins, including an explicit null/zero; never revive a template cap.
  for (const row of [entity, meshTemplateDefinition(entity)]) {
    if (isRecord(row?.item_layer_types)
      && Object.hasOwn(row.item_layer_types, 'carrying_capacity_pounds')) {
      return row.item_layer_types.carrying_capacity_pounds;
    }
    if (isRecord(row) && Object.hasOwn(row, 'carrying_capacity_pounds')) return row.carrying_capacity_pounds;
  }
  return undefined;
}

function firstDefined(record, keys) {
  if (!isRecord(record)) return undefined;
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return undefined;
}

/** No recursive inventory reads: use the viewer's already-authorized projection. */
function meshPresentationValue(axis, options = {}) {
  const entity = options.entity;
  if (axis === 'quantity') {
    const n = scalarNumber(options.quantity ?? entity?.inventory?.item_qty ?? entity?.item_qty);
    return n !== null && Number.isSafeInteger(n) && n >= 0 ? n : null;
  }
  if (axis !== 'fullness') return null;
  const explicit = scalarNumber(options.fullness);
  if (explicit !== null) return Math.max(0, Math.min(1, explicit));
  const held = scalarNumber(entity?.computed?.weight?.held);
  const capacity = scalarNumber(options.capacityLb !== undefined ? options.capacityLb : presentationCapacity(entity));
  return held !== null && held >= 0 && capacity !== null && capacity > 0
    ? Math.min(1, held / capacity) : null;
}

function validPresentationBand(band) {
  if (!isRecord(band) || !TOKEN.test(band.family ?? '')
    || !['quantity', 'fullness'].includes(band.axis)) return false;
  if (Object.hasOwn(band, 'level')) {
    return band.axis === 'fullness' && Number.isInteger(band.level) && band.level >= 0 && band.level <= 4
      && !Object.hasOwn(band, 'min') && !Object.hasOwn(band, 'max');
  }
  return true
    && Number.isFinite(band.min) && band.min >= 0
    && (band.max === undefined || Number.isFinite(band.max) && band.max > band.min)
    && (band.axis !== 'quantity' || Number.isSafeInteger(band.min)
      && (band.max === undefined || Number.isSafeInteger(band.max)));
}

function applyMeshPresentationBand(all, options, request) {
  // Closed, illusion and explicitly requested damage states retain their own geometry.
  if (request.state !== STATE_FALLBACK) return true;
  const baseRequest = { ...request, state: STATE_FALLBACK };
  const anchor = all.filter(row => specificity(row, baseRequest).every(rank => rank < 9))
    .sort((a, b) => compareRows(a, b, baseRequest))[0];
  if (!validPresentationBand(anchor?.presentation)) return true;
  const band = anchor.presentation;
  const value = meshPresentationValue(band.axis, options);
  if (value === null) return true;
  if (band.axis === 'quantity' && value === 0) return false;
  const levelPolicy = Object.hasOwn(band, 'level');
  const targetValue = levelPolicy ? meshFullnessLevel(value) : value;
  const lower = row => levelPolicy ? row.presentation.level : row.presentation.min;
  const siblings = all.filter(row => validPresentationBand(row.presentation)
    && row.presentation.family === band.family && row.presentation.axis === band.axis
    && Object.hasOwn(row.presentation, 'level') === levelPolicy
    && row.role === request.role && row.state === STATE_FALLBACK
    && (row.bodyProfile === request.bodyProfile || row.bodyProfile === PROFILE_FALLBACK)
    && lower(row) <= targetValue);
  // Prefer a complete band; when a larger authored state is still awaiting approval,
  // retain the nearest smaller approved appearance. Never select an unavailable row.
  siblings.sort((a, b) => {
    const contains = row => levelPolicy ? lower(row) === targetValue : row.presentation.max === undefined || value < row.presentation.max;
    return Number(contains(b)) - Number(contains(a)) || lower(b) - lower(a)
      || compareRows(a, b, { ...baseRequest, variant: null });
  });
  if (siblings[0]) request.variant = token(siblings[0].variant, 'default');
  return true;
}

/**
 * Read the canonical vitality ratio without counting temporary vitality as durability.
 * Returns null when vitality is absent so catalogue previews keep requesting `default`.
 */
function meshVitalityRatio(options = {}) {
  const explicitRatio = scalarNumber(options.vitalityRatio);
  if (explicitRatio !== null) return Math.min(1, Math.max(0, explicitRatio));

  const explicitPercent = scalarNumber(options.vitalityPercent);
  if (explicitPercent !== null) return Math.min(1, Math.max(0, explicitPercent / 100));

  const entity = isRecord(options.entity) ? options.entity : null;
  const computed = isRecord(entity?.computed) ? entity.computed : null;
  const stores = [
    options.vitality,
    computed?.vitality,
    computed?.v_arr,
    entity?.vitality,
    entity?.v_arr,
  ];
  for (const store of stores) {
    if (!isRecord(store)) continue;
    const currentRaw = firstDefined(store, ['v_current', 'base_v_current']);
    if (currentRaw === undefined) continue;
    const current = scalarNumber(currentRaw);
    if (current === null) continue;

    let maximum = scalarNumber(firstDefined(store, ['effective_v_max']));
    if (maximum === null) {
      const baseMaximum = scalarNumber(firstDefined(store, ['base_v_max', 'v_max']));
      if (baseMaximum === null) continue;
      const maximumTemp = scalarNumber(firstDefined(store, ['base_v_max_temp', 'v_max_temp'])) ?? 0;
      const blighted = scalarNumber(firstDefined(store, ['blighted_v'])) ?? 0;
      maximum = Math.max(1, baseMaximum + maximumTemp - blighted);
    }
    if (maximum <= 0) continue;
    return Math.min(1, Math.max(0, current / maximum));
  }
  return null;
}

/**
 * Durability policy for physical mesh states:
 *   > 50%       default
 *   1% .. 50%   damaged (or broken when that is the only authored damage state)
 *   0%          broken (falling back to damaged)
 *
 * The available-state guard prevents vitality from inventing an unsupported state.
 */
function meshStateForVitalityRatio(ratio, availableStates = []) {
  const available = new Set(availableStates);
  if (!Number.isFinite(ratio)) return DAMAGE_STATE_DEFAULT;
  if (ratio <= 0) {
    if (available.has(DAMAGE_STATE_BROKEN)) return DAMAGE_STATE_BROKEN;
    if (available.has(DAMAGE_STATE_DAMAGED)) return DAMAGE_STATE_DAMAGED;
    return DAMAGE_STATE_DEFAULT;
  }
  if (ratio <= DAMAGED_VITALITY_RATIO) {
    if (available.has(DAMAGE_STATE_DAMAGED)) return DAMAGE_STATE_DAMAGED;
    if (available.has(DAMAGE_STATE_BROKEN)) return DAMAGE_STATE_BROKEN;
  }
  return DAMAGE_STATE_DEFAULT;
}

/**
 * Backslash, C0 controls, space, and DEL are all disqualifying.
 *
 * Browsers normalize "/\evil.example" to "//evil.example" (protocol-relative) and strip
 * tabs/newlines before resolving a URL, so a bare startsWith('/') check would hand an
 * attacker an arbitrary origin. Written as an explicit code-point scan rather than a
 * regex so the source file contains no literal control characters.
 */
function hasUnsafeUrlChars(url) {
  if (url.indexOf('\\') !== -1) return true;
  for (let i = 0; i < url.length; i++) {
    const code = url.charCodeAt(i);
    if (code <= SPACE_CODE || code === DEL_CODE) return true;
  }
  return false;
}

// Must mirror ft_mesh_url_permitted() in includes/functions/db/meshes/meshes_inc.php.
function urlPermitted(url) {
  if (typeof url !== 'string' || !url.length || url.length > 1024) return false;
  if (hasUnsafeUrlChars(url)) return false;
  if (url.startsWith('//')) return false;                 // scheme-relative
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {                 // absolute: must be allowlisted
    return ALLOWED_ORIGINS.some(origin => url.startsWith(origin));
  }
  return url.startsWith('/');                             // root-relative
}

function validMesh(row) {
  if (!isRecord(row)) return false;
  if (row.kind !== 'glb' && row.kind !== 'gltf' && row.kind !== 'manifest') return false;
  if (row.variant !== undefined && !TOKEN.test(row.variant)) return false;
  if (!ROLES.has(row.role) || !TOKEN.test(row.state ?? '')) return false;
  if (!PROFILES.has(row.bodyProfile)) return false;
  if (!TIERS.includes(row.quality)) return false;
  if (row.part !== undefined && !(typeof row.part === 'string' && PART.test(row.part))) return false;
  if (!urlPermitted(row.url)) return false;
  return typeof row.sha256 === 'string' && SHA256.test(row.sha256);
}

function meshOrder(row) {
  const value = Number(row.order);
  return Number.isSafeInteger(value) && value >= 0 ? value : Number.MAX_SAFE_INTEGER;
}

/**
 * Specificity rank, best (lowest) first. `9` means "incompatible" and is filtered out.
 * Quality is ranked separately so it never outranks a semantic match: a low-tier mesh of
 * the RIGHT body profile beats a high-tier mesh of the wrong one.
 */
function specificity(row, request) {
  const variant = token(row.variant, 'default');
  const variantRank = request.variant === null || variant === request.variant ? 0 : 9;
  const roleRank = row.role === request.role ? 0 : 9;
  const state = token(row.state, STATE_FALLBACK);
  const stateRank = state === request.state ? 0 : state === STATE_FALLBACK ? 1 : 9;
  const profile = row.bodyProfile;
  const profileRank = profile === request.bodyProfile ? 0 : profile === PROFILE_FALLBACK ? 1 : 9;
  return [variantRank, roleRank, stateRank, profileRank];
}

function qualityRank(row, request) {
  const index = TIERS.indexOf(row.quality);
  if (request.quality === 'automatic') return index;      // high(0) preferred
  const wanted = TIERS.indexOf(request.quality);
  if (index === wanted) return 0;
  // Prefer stepping DOWN in quality over up: a lighter asset is the safer miss on tablet.
  return index > wanted ? 1 + (index - wanted) : 10 + (wanted - index);
}

function compareRows(a, b, request) {
  const sa = specificity(a, request);
  const sb = specificity(b, request);
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] !== sb[i]) return sa[i] - sb[i];
  }
  if (request.variant === null) {
    // Approving optional capacity looks must not replace an existing static style
    // merely because a new UUID sorts first. A primary row explicitly opts in.
    const optional = row => !row.primary && row.presentation?.axis === 'fullness'
      && Object.hasOwn(row.presentation, 'level');
    if (optional(a) !== optional(b)) return optional(a) ? 1 : -1;
  }
  const qa = qualityRank(a, request);
  const qb = qualityRank(b, request);
  if (qa !== qb) return qa - qb;
  if (Boolean(b.primary) !== Boolean(a.primary)) return b.primary ? 1 : -1;
  const oa = meshOrder(a);
  const ob = meshOrder(b);
  if (oa !== ob) return oa - ob;
  return String(a.id ?? '').localeCompare(String(b.id ?? ''), 'en');
}

/**
 * INVENTORY MESH SELECTION — `options.selectedMeshId`, the twin of `inventory.mesh_id`.
 *
 * ⚠ This is a DIFFERENT contract from `options.meshId`, deliberately. Keep them apart.
 *
 *   options.meshId          EXACT ROW PIN. Used by world props and damage-state labs. It
 *                           replaces variant, role, bodyProfile AND quality with the named
 *                           row's own values, and returns null if that row is gone. Correct
 *                           for a prop: a specific broken table is a specific broken table.
 *
 *   options.selectedMeshId  VARIANT-FAMILY ANCHOR. Used by an inventory instance carrying a
 *                           player's chosen look. It constrains only `variant` (+ the role
 *                           that variant was authored at) and leaves bodyProfile, quality and
 *                           state to the LIVE request.
 *
 * Why the split: the registry stores each body presentation of one garment as its own row
 * (`mesh_body_profile` is part of the natural key), so an exact-row pin would nail a worn
 * tunic to `masculine_standard` forever. A player who picks "tunic 2, blue" and later flips
 * the character to a feminine body must keep tunic-2-blue and get the FITTED mesh for the
 * body they are actually wearing. The variant key is the axis that survives that change;
 * the body profile is a render-time fact about the wearer, not about the choice.
 *
 * Safety: selection can only ever NARROW `all` — the already-compiled, membership-,
 * approval- and deletion-filtered bucket. It never looks a mesh up, so a stored id can
 * never introduce a row the item was not already entitled to render. That is what
 * "fail closed" means here, and it is structural rather than a check that can be forgotten.
 *
 * Degradation is deliberate and reported, never silent-and-invisible:
 *   'applied'    the family serves this body; the choice is honoured.
 *   'unresolved' the stored id is not in this item's compiled bucket (soft-deleted,
 *                unapproved, retargeted, or the row's template was swapped). Fall back to
 *                ordinary resolution — a worn garment must not vanish because a GM retired
 *                one mesh — and surface the state so it is diagnosable.
 *   'unfitted'   the family exists but has no member fitted to the active body profile.
 *                Same fallback, different cause: this one is an AUTHORING gap (the
 *                counterpart presentation has not been imported yet), not data rot.
 *
 * @param {Array} all      valid compiled mesh rows for this item
 * @param {object} options caller request
 * @param {object} request MUTATED in place when a selection applies
 * @returns {null|{status: string, meshId: string, variant?: string}}
 */
function resolveInventoryMeshSelection(all, options, request) {
  const selectedMeshId = typeof options.selectedMeshId === 'string' && options.selectedMeshId.trim()
    ? options.selectedMeshId.trim()
    : null;
  if (selectedMeshId === null) return null;

  // The two modes are MUTUALLY EXCLUSIVE. An exact pin is a stronger, more specific statement than
  // a family anchor, so it wins outright rather than the two half-applying to one request object.
  // (The pin branch below happens to overwrite every field this function sets, so today the result
  // is the same either way — but relying on that is exactly the kind of coupling that breaks the
  // next time either branch is edited.)
  if (typeof options.meshId === 'string' && options.meshId.trim()) return null;

  // Case-INSENSITIVE. The PHP write side uppercases the stored id while the registry hands the
  // compiled bucket whatever case the row was authored with; a strict === would report a perfectly
  // valid choice as 'unresolved' the moment those two disagreed.
  const selectedRow = all.find(
    row => typeof row.id === 'string' && row.id.toLowerCase() === selectedMeshId.toLowerCase(),
  ) ?? null;
  if (selectedRow === null) return { status: 'unresolved', meshId: selectedMeshId };

  const variant = token(selectedRow.variant, 'default');
  const role = selectedRow.role;

  // Does the family have ANY member that can dress the body we are actually rendering?
  // `any` counts: it is the authored "fits every profile" row. State is not tested here —
  // damage states have their own fallback ladder further down and must not veto the choice.
  const fitted = all.some(row => (
    token(row.variant, 'default') === variant
    && row.role === role
    && (row.bodyProfile === request.bodyProfile || row.bodyProfile === PROFILE_FALLBACK)
  ));
  if (!fitted) return { status: 'unfitted', meshId: selectedMeshId, variant };

  // Pin the family. bodyProfile and quality are POINTEDLY not copied from selectedRow —
  // that omission is the whole feature.
  request.variant = variant;
  request.role = role;
  return { status: 'applied', meshId: selectedMeshId, variant };
}

/**
 * Resolve the mesh SET for a request.
 *
 * Returns every member of the winning group, not a single row:
 *   - rows carrying a `part` key are SET MEMBERS -- one winner PER DISTINCT PART, all
 *     rendered together (bracer_left + bracer_right);
 *   - rows with no `part` are ALTERNATIVES -- exactly one wins.
 * A part-keyed group outranks a partless alternative only if it is at least as specific;
 * the comparison is the ordinary rank, so a specific partless mesh still beats a generic set.
 */
function selectMeshSet(itemArr, options = {}) {
  const explicitState = typeof options.state === 'string' && TOKEN.test(options.state);
  const request = {
    variant: typeof options.variant === 'string' && TOKEN.test(options.variant) ? options.variant : null,
    role: ROLES.has(options.role) ? options.role : 'world',
    state: token(options.state, STATE_FALLBACK),
    bodyProfile: PROFILES.has(options.bodyProfile) ? options.bodyProfile : PROFILE_FALLBACK,
    quality: TIERS.includes(options.quality) ? options.quality : 'automatic',
  };

  const pinnedMeshId = typeof options.meshId === 'string' && options.meshId.trim()
    ? options.meshId.trim() : null;
  // PAID COSMETICS are never the free default look (2026-09-25). The compile stamps
  // `paidSkin` on members of a non-free mesh_skin; they stay in the bucket for Mog, and only an
  // explicit exact pin (review surfaces, an entitled Mog resolution) may draw one.
  // The pin admits its whole variant family (a paid bracer pair's other part, its damage states),
  // never another paid look.
  const valid = meshesOf(itemArr).filter(validMesh);
  const pinnedRow = pinnedMeshId === null ? null : valid.find(row => row.id === pinnedMeshId) ?? null;
  const pinnedVariant = pinnedRow ? token(pinnedRow.variant, 'default') : null;
  const all = valid.filter(row => row.paidSkin !== true
    || (pinnedVariant !== null && token(row.variant, 'default') === pinnedVariant));

  // ---- inventory selection (options.selectedMeshId) -------------------------------------
  // Applied BEFORE the exact-pin branch so the two modes never interleave. See
  // resolveInventoryMeshSelection() for the full contract; in short, a stored
  // inventory.mesh_id names a VARIANT FAMILY, not a fitted row, so it constrains `variant`
  // (and `role`) and deliberately leaves bodyProfile/quality/state to the live request.
  const selection = resolveInventoryMeshSelection(all, options, request);

  const requestedMeshId = typeof options.meshId === 'string' && options.meshId.trim()
    ? options.meshId.trim()
    : null;
  const exactMesh = requestedMeshId === null
    ? null
    : all.find(row => row.id === requestedMeshId) ?? null;
  // An explicit id is authoritative. If approval/deletion removes that row from the
  // ordinary compiled bucket, do not silently substitute a different model.
  if (requestedMeshId !== null && exactMesh === null) return null;
  if (exactMesh) {
    // mesh_id is the identity source. These are derived from that exact approved row,
    // never duplicated onto inventory or trusted from a parallel variant string.
    request.variant = token(exactMesh.variant, 'default');
    request.role = exactMesh.role;
    request.bodyProfile = exactMesh.bodyProfile;
    request.quality = exactMesh.quality;
    if (!explicitState && meshVitalityRatio(options) === null) {
      request.state = token(exactMesh.state, STATE_FALLBACK);
    }
  }

  // Visual states are orthogonal to item identity, gameplay quantity and durability.
  // An exact review pin remains exact; a stored family selection follows live quantity.
  if (!exactMesh && !applyMeshPresentationBand(all, { ...options, entity: options.entity ?? itemArr }, request)) return null;

  let anchorMesh = exactMesh?.state === STATE_FALLBACK ? exactMesh : null;
  const vitalityRatio = explicitState ? null : meshVitalityRatio(options);
  if (vitalityRatio !== null) {
    // Choose the ordinary default mesh first, then lock its stable variant before
    // deriving a damage state. A low-vitality 02a must never jump to an 01a broken mesh.
    if (request.variant === null) {
      const anchorRequest = { ...request, state: STATE_FALLBACK };
      const anchor = all
        .filter(row => specificity(row, anchorRequest).every(rank => rank < 9))
        .sort((a, b) => compareRows(a, b, anchorRequest))[0];
      if (!anchor) return null;
      request.variant = token(anchor.variant, 'default');
      anchorMesh = anchor;
    }

    anchorMesh ??= all
      .filter(row => token(row.variant, 'default') === request.variant)
      .filter(row => row.role === request.role)
      .filter(row => token(row.state, STATE_FALLBACK) === STATE_FALLBACK)
      .filter(row => row.bodyProfile === request.bodyProfile || row.bodyProfile === PROFILE_FALLBACK)
      .sort((a, b) => compareRows(a, b, { ...request, state: STATE_FALLBACK }))[0] ?? exactMesh;

    const availableStates = all
      .filter(row => token(row.variant, 'default') === request.variant)
      .filter(row => row.role === request.role)
      .filter(row => row.bodyProfile === request.bodyProfile || row.bodyProfile === PROFILE_FALLBACK)
      .map(row => token(row.state, STATE_FALLBACK));
    request.state = meshStateForVitalityRatio(vitalityRatio, availableStates);
  }

  const candidates = all
    .filter(row => specificity(row, request).every(rank => rank < 9))
    .sort((a, b) => compareRows(a, b, request));

  if (!candidates.length) return null;

  const best = exactMesh && vitalityRatio === null
    && token(exactMesh.state, STATE_FALLBACK) === request.state
    ? exactMesh
    : candidates[0];
  request.variant = token(best.variant, 'default');
  anchorMesh ??= all
    .filter(row => token(row.variant, 'default') === request.variant)
    .filter(row => row.role === request.role)
    .filter(row => token(row.state, STATE_FALLBACK) === STATE_FALLBACK)
    .filter(row => row.bodyProfile === request.bodyProfile || row.bodyProfile === PROFILE_FALLBACK)
    .sort((a, b) => compareRows(a, b, { ...request, state: STATE_FALLBACK }))[0] ?? exactMesh ?? best;
  if (best.part === undefined) {
    return {
      meshes: [best],
      requested: request,
      variant: request.variant,
      anchorMeshId: anchorMesh.id,
      activeMeshIds: [best.id],
      set: false,
      selection,
    };
  }

  // Set: resolve EACH PART INDEPENDENTLY against the full ranked candidate list.
  //
  // An earlier version restricted members to the winner's exact specificity, to stop a
  // "stray" low-specificity part joining the group. That risk is imaginary -- parts are
  // authored deliberately -- and the restriction caused a real, visible bug: an
  // exact-profile bracer_left plus an `any` bracer_right rendered only the LEFT bracer,
  // so the character wore one bracer (Codex review 2026-08-03). Per-part fallback is both
  // simpler and correct: a lower-specificity right bracer beats no right bracer.
  const byPart = new Map();
  const seenIds = new Set();
  for (const row of candidates) {
    if (row.part === undefined) continue;
    if (byPart.has(row.part)) continue;              // candidates are pre-sorted: first = best
    if (seenIds.has(row.id)) continue;               // never render one mesh twice in a set
    byPart.set(row.part, row);
    seenIds.add(row.id);
  }

  const selectedMeshes = [...byPart.values()];
  return {
    meshes: selectedMeshes,
    requested: request,
    variant: request.variant,
    anchorMeshId: anchorMesh.id,
    activeMeshIds: selectedMeshes.map(row => row.id),
    set: true,
    parts: [...byPart.keys()],
    selection,
  };
}

/** Native inventory selection, independent of any encounter or review route. */
function selectInventoryMeshSet(itemArr, options = {}) {
  // Worn fittings are a separate runtime from world props. When the caller supplies
  // the current approved-target map, authoring provenance must not rank or exclude looks.
  // Keep explicitly selected anchors even when unavailable: never substitute another look.
  if (options.approvedFittings) {
    const rawChoice = options.selectedMeshId ?? itemArr?.inventory?.mesh_id ?? itemArr?.mesh_id;
    const choice = Array.isArray(rawChoice) ? rawChoice[0] : rawChoice;
    const rows = meshesOf(itemArr)
      .filter(row => choice || options.approvedFittings[row.id?.toUpperCase()]?.[options.bodyProfile])
      .map(row => ({ ...row, bodyProfile: PROFILE_FALLBACK }));
    itemArr = { ...itemArr, assets: { ...itemArr?.assets, '3d': rows } };
  }
  const rawId = options.selectedMeshId ?? itemArr?.inventory?.mesh_id ?? itemArr?.mesh_id;
  const selectedMeshId = Array.isArray(rawId) ? rawId[0] : rawId;
  const selected = typeof selectedMeshId === 'string'
    ? meshesOf(itemArr).find(row => row.id?.toUpperCase() === selectedMeshId.toUpperCase()) : null;
  const state = selected?.state;
  let vitality;
  if (options.vitality === undefined && options.vitalityRatio === undefined && options.vitalityPercent === undefined
    && isRecord(itemArr?.computed?.vitality)) {
    // Compiled inventory keeps only overrides; the definition holds the ordinary maximum.
    const defaults = meshTemplateDefinition(itemArr)?.computed?.vitality;
    vitality = { ...(isRecord(defaults) ? defaults : {}), ...itemArr.computed.vitality };
    // Older full definitions also carry v_current; either row current alias wins over it.
    const current = firstDefined(itemArr.computed.vitality, ['v_current', 'base_v_current']);
    if (current !== undefined) vitality.v_current = current;
  }
  return selectMeshSet(itemArr, {
    selectedMeshId,
    entity: itemArr,
    role: 'world',
    // Special looks stay explicit; ordinary damage states follow the live vitality reader.
    ...(state && !['default', 'damaged', 'broken'].includes(state) ? { state } : {}),
    ...(vitality ? { vitality } : {}),
    ...options,
  });
}

/**
 * INSTANCE art first, then template art. The superseded resolver only ever looked at
 * pictures.item, so a GM's per-token upload was invisible.
 */
function instancePicturesOf(itemArr) {
  const bucket = isRecord(itemArr?.pictures) ? itemArr.pictures : {};
  const out = [];
  for (const key of [
    'inventory_presentation_picture',
    'encounter_creature_picture',
    'inventory_picture',
    'inventory_thumbnail_picture',
  ]) {
    if (Array.isArray(bucket[key])) out.push(...bucket[key]);
  }
  return out;
}

function templatePicturesOf(itemArr) {
  const bucket = isRecord(itemArr?.pictures) ? itemArr.pictures : {};
  return Array.isArray(bucket.item) ? bucket.item : [];
}

function pickPicture(rows) {
  const usable = rows.filter(row => (
    isRecord(row) && typeof row.picture_url === 'string' && row.picture_url.length > 0
  ));
  return usable.find(row => Number(row.primary_picture ?? 0) === 1) ?? usable[0] ?? null;
}

function itemLabel(itemArr) {
  const label = isRecord(itemArr?.items) ? itemArr.items.item_name : null;
  return typeof label === 'string' && label.trim() ? label.trim() : 'Unknown item';
}

/**
 * Deterministic presentation precedence (owner-facing behaviour):
 *   1. mesh set from the item (3D wins when it exists)
 *   2. INSTANCE picture as a standee   <- new; a GM's own upload is a BETTER standee
 *   3. template item picture as a standee
 *   4. generated labeled standee
 * A missing or invalid mesh never makes an entity unrenderable.
 */
function resolveItemPresentation(itemArr, options = {}) {
  const selected = selectMeshSet(itemArr, options);
  if (selected) {
    return {
      mode: selected.meshes[0].kind === 'manifest' ? 'manifest' : 'glb',
      source: 'mesh',
      meshes: selected.meshes,
      set: selected.set,
      parts: selected.parts,
      requested: selected.requested,
      selection: selected.selection,
    };
  }

  for (const [rows, source] of [
    [instancePicturesOf(itemArr), 'instance-picture'],
    [templatePicturesOf(itemArr), 'item-picture'],
  ]) {
    const picture = pickPicture(rows);
    if (picture) {
      return {
        mode: 'standee',
        source,
        url: picture.picture_url,
        picture,
        label: itemLabel(itemArr),
      };
    }
  }

  return {
    mode: 'standee',
    source: 'generated-standee',
    label: itemLabel(itemArr),
    generator: {
      kind: 'labeled-marker',
      deterministicKey: String(itemArr?.items?.item_id ?? itemLabel(itemArr)),
    },
  };
}

export {
  hasUnsafeUrlChars,
  resolveInventoryMeshSelection,
  instancePicturesOf,
  meshesOf,
  meshStateForVitalityRatio,
  meshVitalityRatio,
  meshPresentationValue,
  meshFullnessLevel,
  resolveItemPresentation,
  selectMeshSet,
  selectInventoryMeshSet,
  templatePicturesOf,
  urlPermitted,
  validMesh,
};
